
import React from 'react';
import type { Feature } from '../../types.ts';
import { RAW_FEATURES } from './manifest.ts';
import { lazyWithRetry } from '../../services/componentLoader.ts';

// Map feature IDs to their components using lazy loading with retry logic
const componentMap: Record<string, React.FC<any>> = {
    'ai-command-center': lazyWithRetry(() => import('@/components/features/AiCommandCenter.tsx'), 'AiCommandCenter'),
    'ai-code-explainer': lazyWithRetry(() => import('@/components/AiCodeExplainer.tsx'), 'AiCodeExplainer'),
    'ai-feature-builder': lazyWithRetry(() => import('@/components/features/AiFeatureBuilder.tsx'), 'AiFeatureBuilder'),
    'regex-sandbox': lazyWithRetry(() => import('@/components/features/RegexSandbox.tsx'), 'RegexSandbox'),
    'portable-snippet-vault': lazyWithRetry(() => import('@/components/features/SnippetVault.tsx'), 'SnippetVault'),
    'css-grid-editor': lazyWithRetry(() => import('@/components/features/CssGridEditor.tsx'), 'CssGridEditor'),
    'ai-commit-generator': lazyWithRetry(() => import('@/components/AiCommitGenerator.tsx'), 'AiCommitGenerator'),
    'json-tree-navigator': lazyWithRetry(() => import('@/components/features/JsonTreeNavigator.tsx'), 'JsonTreeNavigator'),
    'ai-unit-test-generator': lazyWithRetry(() => import('@/components/features/AiUnitTestGenerator.tsx'), 'AiUnitTestGenerator'),
    'prompt-craft-pad': lazyWithRetry(() => import('@/components/features/PromptCraftPad.tsx'), 'PromptCraftPad'),
    'linter-formatter': lazyWithRetry(() => import('@/components/features/CodeFormatter.tsx'), 'CodeFormatter'),
    'schema-designer': lazyWithRetry(() => import('@/components/features/SchemaDesigner.tsx'), 'SchemaDesigner'),
    'pwa-manifest-editor': lazyWithRetry(() => import('@/components/features/PwaManifestEditor.tsx'), 'PwaManifestEditor'),
    'markdown-slides-generator': lazyWithRetry(() => import('@/components/features/MarkdownSlides.tsx'), 'MarkdownSlides'),
    'screenshot-to-component': lazyWithRetry(() => import('@/components/features/ScreenshotToComponent.tsx'), 'ScreenshotToComponent'),
    'project-moodboard': lazyWithRetry(() => import('@/components/features/ProjectMoodboard.tsx'), 'ProjectMoodboard'),
    'dev-notes-sticky-panel': lazyWithRetry(() => import('@/components/features/DevNotesStickyPanel.tsx'), 'DevNotesStickyPanel'),
    'font-preview-picker': lazyWithRetry(() => import('@/components/features/FontPreviewPicker.tsx'), 'FontPreviewPicker'),
    'theme-designer': lazyWithRetry(() => import('@/components/features/ThemeDesigner.tsx'), 'ThemeDesigner'),
    'svg-path-editor': lazyWithRetry(() => import('@/components/features/SvgPathEditor.tsx'), 'SvgPathEditor'),
    'ai-style-transfer': lazyWithRetry(() => import('@/components/features/AiStyleTransfer.tsx'), 'AiStyleTransfer'),
    'ai-coding-challenge': lazyWithRetry(() => import('@/components/features/AiCodingChallenge.tsx'), 'AiCodingChallenge'),
    'font-pairing-tool': lazyWithRetry(() => import('@/components/features/FontPairingTool.tsx'), 'FontPairingTool'),
    'code-review-bot': lazyWithRetry(() => import('@/components/features/CodeReviewBot.tsx'), 'CodeReviewBot'),
    'pr-summary-generator': lazyWithRetry(() => import('@/components/features/PrSummaryGenerator.tsx'), 'PrSummaryGenerator'),
    'changelog-generator': lazyWithRetry(() => import('@/components/features/ChangelogGenerator.tsx'), 'ChangelogGenerator'),
    'cron-job-builder': lazyWithRetry(() => import('@/components/features/CronJobBuilder.tsx'), 'CronJobBuilder'),
    // --- New & Upgraded Features ---
    'ai-code-migrator': lazyWithRetry(() => import('@/components/features/AiCodeMigrator.tsx'), 'AiCodeMigrator'),
    'visual-git-tree': lazyWithRetry(() => import('@/components/features/VisualGitTree.tsx'), 'VisualGitTree'),
    'worker-thread-debugger': lazyWithRetry(() => import('@/components/features/WorkerThreadDebugger.tsx'), 'WorkerThreadDebugger'),

};

// Map feature IDs to specific AI configurations
const aiConfigMap: Record<string, Feature['aiConfig']> = {
    'ai-code-explainer': { model: 'gemini-2.5-flash' },
    'ai-feature-builder': { model: 'gemini-2.5-flash' },
    'ai-commit-generator': { model: 'gemini-2.5-flash' },
    'ai-unit-test-generator': { model: 'gemini-2.5-flash' },
    'regex-sandbox': { model: 'gemini-2.5-flash' },
    'screenshot-to-component': { model: 'gemini-2.5-flash' },
    'theme-designer': { model: 'gemini-2.5-flash' },
    'ai-style-transfer': { model: 'gemini-2.5-flash' },
    'ai-coding-challenge': { model: 'gemini-2.5-flash' },
    'code-review-bot': { model: 'gemini-2.5-flash' },
    'pr-summary-generator': { model: 'gemini-2.5-flash' },
    'ai-code-migrator': { model: 'gemini-2.5-flash' },
    'visual-git-tree': { model: 'gemini-2.5-flash' },
    'worker-thread-debugger': { model: 'gemini-2.5-flash' },
    'ai-command-center': { model: 'gemini-2.5-flash' },
};

const Placeholder = lazyWithRetry(() => import('./Placeholder.tsx'), 'Placeholder');

// "Compile" the raw data into the final Feature objects
export const ALL_FEATURES: Feature[] = RAW_FEATURES.map(feature => ({
    ...feature,
    component: componentMap[feature.id] || Placeholder,
    aiConfig: aiConfigMap[feature.id] || { model: 'gemini-2.5-flash' },
}));

// Create a simple map for quick lookups
export const FEATURES_MAP = new Map(ALL_FEATURES.map(f => [f.id, f]));
